var __defProp = Object.defineProperty;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __publicField = (obj, key, value) => {
  __defNormalProp(obj, typeof key !== "symbol" ? key + "" : key, value);
  return value;
};
import Ajv from "ajv";
import { clone } from "../utils/clone.js";
import * as bitecs from "bitecs";
import { StringToEnum } from "../utils/typehelpers.js";
import { C as ComponentCategory } from "../enums-86f58524.js";
import { a as assignGlobalSingleton } from "../global-33d5eebf.js";
class Schema {
  constructor(args) {
    if (this.constructor.__constructables) {
      Object.entries(this.constructor.__constructables).forEach(
        // @ts-ignore
        ([key, Constructor]) => {
          if (Array.isArray(this[key])) {
            this[key] = this[key].map((x) => new Constructor(x));
          } else if (this[key] !== void 0) {
            this[key] = new Constructor(this[key]);
          }
        }
      );
    }
  }
  toJSON() {
    const data = {};
    Object.entries(this).forEach(([key, value]) => {
      if (typeof value === "object" && (value == null ? void 0 : value.toJSON)) {
        data[key] = value.toJSON();
      } else {
        data[key] = value;
      }
    });
    return data;
  }
}
class BitecsSchema extends Schema {
  static encode(ids, snapshot = false) {
    const bitecsKeys = Object.keys(this.__bitecs).filter((x) => !x.startsWith("_"));
    let size = 4;
    let entityCount = 0;
    for (let i = 0; i < ids.length; ++i) {
      if (this.store.__changes[ids[i]]) {
        size += 4;
        const change = this.store.__changes[ids[i]];
        if (change) {
          entityCount++;
          for (let j = 0; j < bitecsKeys.length; j++) {
            if (snapshot) {
              this.store.__changes[ids[i]] |= 1 << j;
            }
            if (change & 1 << j || snapshot) {
              size += 4;
            }
          }
        }
      }
    }
    const buffer = new ArrayBuffer(size);
    let offset = 0;
    const componentIdSlot = new Uint16Array(buffer, offset, 1);
    componentIdSlot[0] = this.__id;
    offset += 2;
    const entityCountSlot = new Uint16Array(buffer, offset, 1);
    entityCountSlot[0] = entityCount;
    offset += 2;
    for (let i = 0; i < ids.length; ++i) {
      if (this.store.__changes[ids[i]]) {
        const idSlot = new Uint16Array(buffer, offset, 1);
        idSlot[0] = ids[i];
        offset += 2;
        const changeSlot = new Uint16Array(buffer, offset, 1);
        changeSlot[0] = this.store.__changes[ids[i]];
        offset += 2;
        const change = this.store.__changes[ids[i]];
        this.store.__changes[ids[i]] = 0;
        for (let j = 0; j < bitecsKeys.length; j++) {
          if (change & 1 << j) {
            let valueSlot;
            const bitecsType = this.__bitecs[bitecsKeys[j]];
            switch (bitecsType) {
              case "ui8":
              case "ui16":
              case "ui32":
                valueSlot = new Uint32Array(buffer, offset, 1);
                break;
              case "i8":
              case "i16":
              case "i32":
                valueSlot = new Int32Array(buffer, offset, 1);
                break;
              case "f32":
                valueSlot = new Float32Array(buffer, offset, 1);
                break;
              default:
                throw new Error("Incompatible bitecs type" + bitecsType);
            }
            valueSlot[0] = this.store[bitecsKeys[j]][ids[i]];
            offset += 4;
          }
        }
      }
    }
    return buffer;
  }
  static decode(buffer) {
    let offset = 2;
    const entityCountSlot = new Uint16Array(buffer, offset, 1);
    offset += 2;
    const entityCount = entityCountSlot[0];
    for (let i = 0; i < entityCount; ++i) {
      const idSlot = new Uint16Array(buffer, offset, 1);
      const id = idSlot[0];
      offset += 2;
      const changeSlot = new Uint16Array(buffer, offset, 1);
      const change = changeSlot[0];
      offset += 2;
      const bitecsKeys = Object.keys(this.__bitecs).filter((x) => !x.startsWith("_"));
      for (let j = 0; j < bitecsKeys.length; j++) {
        if (change & 1 << j) {
          let valueSlot;
          const bitecsType = this.__bitecs[bitecsKeys[j]];
          switch (bitecsType) {
            case "ui8":
            case "ui16":
            case "ui32":
              valueSlot = new Uint32Array(buffer, offset, 1);
              break;
            case "i8":
            case "i16":
            case "i32":
              valueSlot = new Int32Array(buffer, offset, 1);
              break;
            case "f32":
              valueSlot = new Float32Array(buffer, offset, 1);
              break;
            default:
              throw new Error("Incompatible bitecs type" + bitecsType);
          }
          this.store[bitecsKeys[j]][id] = valueSlot[0];
          offset += 4;
        }
      }
    }
  }
}
__publicField(BitecsSchema, "store");
__publicField(BitecsSchema, "id");
class TypeSchema extends Schema {
  constructor() {
    super(...arguments);
    __publicField(this, "type");
  }
}
const componentStringSchema = assignGlobalSingleton(
  "componentStringSchema",
  () => /* @__PURE__ */ new Map()
);
const componentIdSchema = assignGlobalSingleton("componentIdSchema", () => /* @__PURE__ */ new Map());
const componentSchemaString = assignGlobalSingleton(
  "componentSchemaString",
  () => /* @__PURE__ */ new Map()
);
const syncableComponents = assignGlobalSingleton(
  "syncableComponents",
  () => /* @__PURE__ */ new Set()
);
const bitecsComponents = assignGlobalSingleton("bitecsComponents", () => /* @__PURE__ */ new Set());
function Component(name, category = ComponentCategory.NONE) {
  return function(cls) {
    if (!cls.__category) {
      if (typeof category === "string") {
        cls.__category = StringToEnum(category, ComponentCategory) ?? ComponentCategory.NONE;
      } else {
        cls.__category = category;
      }
    }
    if (componentStringSchema.has(name)) {
      const existingSchema = componentStringSchema.get(name);
      cls.__schema = existingSchema.__schema;
      cls.__id = existingSchema.__id;
      cls.__type = existingSchema.__type;
      cls.__validate = existingSchema.__validate;
      componentSchemaString.set(cls, name);
      componentStringSchema.set(name, cls);
      return;
    }
    if (!cls.__schema) {
      cls.__schema = false;
      componentSchemaString.set(cls, name);
      componentStringSchema.set(name, cls);
      cls.__id = componentSchemaString.size;
      cls.__type = name;
      cls.__validate = () => true;
      componentIdSchema.set(cls.__id, cls);
      return;
    }
    generateSchema({ constructor: cls }).setType("type", "string");
    componentSchemaString.set(cls, name);
    componentStringSchema.set(name, cls);
    cls.__id = componentSchemaString.size;
    cls.__type = name;
    componentIdSchema.set(cls.__id, cls);
    try {
      const validate = ajv.compile(cls.__schema);
      cls.__validate = validate;
    } catch (e) {
      console.error(e);
      console.error(cls.__schema);
      throw e;
    }
  };
}
function Bitecs() {
  return function(cls) {
    bitecsComponents.add(cls);
    cls.__bitecs.__changes = "ui32";
    const type2 = cls.__type;
    cls.store = assignGlobalSingleton(type2 + "bitec_store", () => bitecs.defineComponent(cls.__bitecs, 0));
  };
}
const altNumberTypes = ["int8", "uint8", "int16", "uint16", "int32", "uint32", "int64", "uint64", "float32", "float64"];
const simpleToBitecs = {
  int8: bitecs.Types.i8,
  uint8: bitecs.Types.ui8,
  int16: bitecs.Types.i16,
  uint16: bitecs.Types.ui16,
  int32: bitecs.Types.i32,
  uint32: bitecs.Types.ui32,
  float32: bitecs.Types.f32,
  float64: bitecs.Types.f64
};
const convertToBitecs = (value) => {
  return simpleToBitecs[value];
};
const ajv = new Ajv({ useDefaults: true, strict: false, allErrors: true });
const generateSchema = (target) => {
  if (target.constructor.__schema && target.constructor.__schema.$comment !== target.constructor.name) {
    target.constructor.__schema = clone(target.constructor.__schema);
    target.constructor.__schema.$comment = target.constructor.name;
    target.constructor.compileDebounce = null;
  } else {
    target.constructor.__schema = target.constructor.__schema || {
      type: "object",
      additionalProperties: false,
      $comment: target.constructor.name
    };
  }
  target.constructor.__schema.properties = target.constructor.__schema.properties || {};
  target.constructor.__schema.required = target.constructor.__schema.required || [];
  return {
    setEnum: (propertyKey, enumToCheck) => {
      if (!target.constructor.__schema.properties[propertyKey]) {
        target.constructor.__schema.properties[propertyKey] = {
          type: "number"
        };
      }
      target.constructor.__schema.properties[propertyKey].enum = [];
      target.constructor.__schema.properties[propertyKey].options = {
        enum_titles: []
      };
      for (const [key, value] of Object.entries(enumToCheck)) {
        if (typeof value === "number") {
          target.constructor.__schema.properties[propertyKey].options.enum_titles.push(key);
          target.constructor.__schema.properties[propertyKey].enum.push(value);
        }
      }
    },
    setDefault: (key, value) => {
      target.constructor.__schema.properties[key] = {
        ...target.constructor.__schema.properties[key] ?? {
          type: typeof value
        },
        default: value
      };
    },
    setRequired: (key) => {
      if (!target.constructor.__schema.required.includes(key)) {
        target.constructor.__schema.required.push(key);
      }
    },
    setType: (key, type2) => {
      var _a;
      const prevType = (_a = target.constructor.__schema.properties[key]) == null ? void 0 : _a.type;
      let setType = altNumberTypes.includes(type2) ? "number" : type2;
      if (prevType && prevType !== type2) {
        if (Array.isArray(prevType)) {
          setType = prevType.concat(type2);
        } else {
          setType = [prevType, setType];
        }
      }
      target.constructor.__schema.properties[key] = {
        ...target.constructor.__schema.properties[key] ?? {},
        type: setType
      };
    },
    setArrayType: function(key, type2) {
      if (typeof type2 !== "string") {
        target.constructor.__constructables = target.constructor.__constructables || {};
        target.constructor.__constructables[key] = type2;
      }
      if (typeof type2 === "string") {
        target.constructor.__schema.properties[key] = {
          ...target.constructor.__schema.properties[key] ?? {},
          type: "array",
          items: {
            type: altNumberTypes.includes(type2) ? "number" : type2
          }
        };
      } else {
        target.constructor.__schema.properties[key] = {
          ...target.constructor.__schema.properties[key] ?? {},
          type: "array",
          items: {
            type: "object",
            properties: type2.__schema.properties,
            required: type2.__schema.required,
            additionalProperties: false
          }
        };
      }
    },
    setObjectType: (key, type2) => {
      target.constructor.__constructables = target.constructor.__constructables || {};
      target.constructor.__constructables[key] = type2;
      target.constructor.__schema.properties[key] = {
        ...target.constructor.__schema.properties[key] ?? {},
        type: "object",
        properties: type2.__schema.properties,
        required: type2.__schema.required,
        additionalProperties: false
      };
    },
    setMapType: (key, type2) => {
      target.constructor.__schema.properties[key] = {
        ...target.constructor.__schema.properties[key] ?? {},
        type: "object",
        patternProperties: {
          ".*": typeof type2 === "string" ? { type: altNumberTypes.includes(type2) ? "number" : type2 } : {
            type: "object",
            properties: type2.__schema.properties,
            required: type2.__schema.required,
            additionalProperties: false
          }
        },
        properties: void 0,
        required: void 0,
        additionalProperties: false
      };
      delete target.constructor.__schema.properties[key].properties;
      delete target.constructor.__schema.properties[key].required;
    }
  };
};
const isRecord = (value) => {
  if (typeof value !== "object") {
    return false;
  }
  if (Array.isArray(value)) {
    return false;
  }
  const keys = Object.keys(value);
  const numberKeys = keys.filter((key) => !isNaN(Number(key)));
  const stringKeys = keys.filter((key) => isNaN(Number(key)));
  return numberKeys.length === stringKeys.length;
};
function type(type2) {
  return function(target, key) {
    if (typeof key === "string" && key.startsWith("_")) {
      key = key.substring(1);
    }
    const schema = generateSchema(target);
    if (isRecord(type2)) {
      target.constructor.__bitecs = target.constructor.__bitecs || {};
      target.constructor.__bitecs[key] = convertToBitecs("uint8");
      schema.setType(key, "number");
      schema.setEnum(key, type2);
    } else if (type2 === "Entity" || type2 === "EntityArray" || Array.isArray(type2) && type2[0] === "Entity") {
      if (type2 === "Entity") {
        schema.setType(key, "number");
      } else {
        schema.setArrayType(key, "number");
      }
      target.constructor.__entityTypes = target.constructor.__entityTypes || {};
      target.constructor.__entityTypes[key] = type2;
    } else if (Array.isArray(type2)) {
      schema.setArrayType(key, type2[0]);
    } else if (typeof type2 === "object" && (type2 == null ? void 0 : type2.set)) {
      schema.setArrayType(key, type2.set);
    } else if (typeof type2 === "function") {
      schema.setObjectType(key, type2);
    } else {
      target.constructor.__bitecs = target.constructor.__bitecs || {};
      target.constructor.__bitecs[key] = convertToBitecs(type2);
      schema.setType(key, altNumberTypes.includes(type2) ? "number" : type2);
    }
  };
}
function defaultValue(value) {
  return function(target, key) {
    const schema = generateSchema(target);
    if (typeof key === "string" && key.startsWith("_")) {
      key = key.substring(1);
    }
    schema.setDefault(key, value);
    if (typeof key === "string") {
      schema.setRequired(key);
    }
  };
}
function required() {
  return function(target, key) {
    generateSchema(target).setRequired(key);
  };
}
function Enum(enumToCheck) {
  return function(target, key) {
    generateSchema(target).setEnum(key, enumToCheck);
  };
}
function mapType(type2) {
  return function(target, key) {
    generateSchema(target).setMapType(key, type2);
  };
}
function nullable() {
  return function(target, key) {
    generateSchema(target).setType(key, "null");
  };
}
export {
  Bitecs,
  BitecsSchema,
  Component,
  Enum,
  Schema,
  TypeSchema,
  bitecsComponents,
  componentIdSchema,
  componentSchemaString,
  componentStringSchema,
  defaultValue,
  mapType,
  nullable,
  required,
  syncableComponents,
  type
};
//# sourceMappingURL=type.js.map
